import styled from '@emotion/styled';
import { Menu, MenuItem } from '@mui/material';
import { PlainIconButton } from '@shared/components/core/button/PlainIconButton';
import Icons from '@shared/components/core/images/Icons';
import SrOnlyText from '@shared/components/core/text/SrOnlyText';
import { useAuthStore } from '@shared/hooks/state/AuthContainer';
import { useCurrentUserStore } from '@shared/hooks/state/CurrentUserContainer';
import { useNotificationStore } from '@shared/hooks/state/NotificationContainer';
import useSignout from '@shared/hooks/useSignout';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import ContentDialog from '../core/dialog/ContentDialog';
import { TextSize90 } from '../core/text/Typography';
import LocalizationEditDialog from '../localization/LocalizationEditDialog';

interface IUserMenuButtonProps {
    settingsHref: string;
    skipConfirmation?: boolean;
    notificationHref: string;
    otherContent?: React.ReactNode;
}

export default function UserMenuButton({
    settingsHref,
    notificationHref,
    skipConfirmation = true,
    otherContent,
}: IUserMenuButtonProps) {
    const { t } = useTranslation();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const navigate = useNavigate();
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
    const isEmailVerified = useCurrentUserStore((state) => state.isEmailVerified);
    const { signout, isSignOutConfirmationVisible, showSignoutConfirmation, hideSignoutConfirmation } = useSignout();
    const unreadNotifications = useNotificationStore((state) => state.unreadNotifications);
    const [showLocalizationDialog, setShowLocalizationDialog] = useState(false);

    function handleClick(event: any) {
        if (anchorEl !== event.currentTarget) {
            setAnchorEl(event.currentTarget);
        }
    }
    function handleClose() {
        setAnchorEl(null);
    }

    function handleSettingsClick() {
        handleClose();
        navigate(settingsHref);
    }

    function handleLogoutClick() {
        handleClose();
        showSignoutConfirmation();
    }

    function handleNotificationClick() {
        handleClose();
        navigate(notificationHref);
    }

    return (
        <>
            <IconButton
                id="user-menu"
                aria-haspopup="true"
                aria-expanded={anchorEl ? 'true' : 'false'}
                onClick={handleClick}
                onMouseOver={handleClick}>
                <SrOnlyText>{t('common:account.menu')}</SrOnlyText>
                <Icons size={24} name="user" />
                {unreadNotifications.length > 0 && <NotificationIndicator aria-hidden="true" />}
            </IconButton>
            <Menu
                closeAfterTransition={false}
                aria-labelledby="user-menu"
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                slotProps={{ list: { onMouseLeave: handleClose } }}>
                {otherContent}
                <StyledMenuItem onClick={() => setShowLocalizationDialog(true)}>
                    <Icons name="globe" size={20} />
                    {t('common:localization.titleShort')}
                </StyledMenuItem>
                {isAuthenticated && (
                    <>
                        {isEmailVerified && (
                            <>
                                <StyledMenuItem onClick={handleNotificationClick}>
                                    <Icons name="alert" size={20} />
                                    {t('common:notifications.title')}
                                </StyledMenuItem>
                                <StyledMenuItem onClick={handleSettingsClick}>
                                    <Icons name="settings" size={20} />
                                    {t('common:account.settings')}
                                </StyledMenuItem>
                            </>
                        )}
                        <SignOutMenuItem onClick={skipConfirmation ? signout : handleLogoutClick}>
                            <Icons name="logout" />
                            {t('common:account.signout.title')}
                        </SignOutMenuItem>
                    </>
                )}
            </Menu>
            <LocalizationEditDialog open={showLocalizationDialog} onClose={() => setShowLocalizationDialog(false)} />
            <ContentDialog
                open={isSignOutConfirmationVisible}
                onCancel={hideSignoutConfirmation}
                title={t('common:account.signout.question')}
                description={t('common:account.signout.description')}
                onConfirm={signout}
                confirmButtonText={t('common:account.signout.confirm')}
                cancelButtonText={t('common:cancel')}
            />
        </>
    );
}

const StyledMenuItem = styled(MenuItem)`
    display: flex;
    align-items: center;
    gap: 8px;
    font-weight: 300;
    ${TextSize90}
    letter-spacing: 0.03rem;
    &:hover {
        color: ${(props) => props.theme.color.accent};
        text-decoration: underline;
    }
`;

const SignOutMenuItem = styled(StyledMenuItem)`
    margin-top: 1rem;
    padding-top: 0.5rem;
    border-top: 1px solid ${(props) => props.theme.input.border};
`;

const NotificationIndicator = styled.span`
    position: absolute;
    top: 5px;
    right: 5px;
    width: 10px;
    height: 10px;
    background-color: ${(props) => props.theme.color.link};
    border-radius: 50%;
    pointer-events: none; /* Ensure it doesn't interfere with clicks */
`;

const IconButton = styled(PlainIconButton)`
    color: ${(props) => props.theme.color.text};
`;
